import type React from "react";
import { useState } from "react";
import { Box, Drawer, IconButton } from "@mui/material";
import { MenuOutlined } from "@mui/icons-material";
import { TrendingUp, Assessment } from "@mui/icons-material";
import { SidebarMenuItem, StagingConfig } from "./sidebar/types/sidebar";
import { BaseURL } from "../service/Api";
import { StagingTag } from "./sidebar/StagingTag";
import { CollapseButton } from "./sidebar/CollapsibleButton";
import { SidebarMenu } from "./sidebar/SidebarMenu";
import { ProfileSection } from "./sidebar/ProfileButton";
import { SidebarContext } from "./sidebar/SidebarContext";
import {
  LocationIcon,
  CrewIcon,
  DocumentIcon,
} from "../components/icons/index";
import {
  OperationsIcon,
  DashboardIcon,
  GearIcon,
  SettingsIcon,
  SalesIcon,
  AllCrewIcon,
  BookingsIcon,
  ContactIcon,
  ConfigurationIcon,
  MemoryIcon,
  DocumentLikeIcon,
  AcademyIcon,
  UserAdminIcon,
  EndpointRequestIcon,
} from "./sidebar/icons/index";
import { AutologIcon } from "./sidebar/icons/AutologIcon";
import { useSelector } from "react-redux";
import { RootState } from "../store/Index";
// import Chema from "../assets/logo/Chema.png";

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 80;

const STAGING_CONFIG: StagingConfig = {
  isStaging: BaseURL === "https://staging.muzukuru.com",
  borderColor: "#f59e0b",
  glowColor: "rgba(245, 158, 11, 0.25)",
  badgeColor: "#fbbf24",
  badgeText: "STAGING",
};

interface SidebarProps {
  theme?: any;
  user?: any;
  userPermissions?: string[];
}

const Sidebar: React.FC<SidebarProps> = ({
  theme = {
    neutral_colors: {
      100: "#000000",
      400: "#9ca3af",
      800: "#614438",
      900: "#ffffff",
    },
    primary_colors: { 400: "#ffffff" },
  },
  user = null,
  userPermissions = [],
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    Sales: false,
    Operations: false,
  });

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const hasPermission = (permission?: string) => {
    if (!permission) return true;
    return userPermissions.includes(permission.trim().replace(/'$/, ""));
  };

  const mainMenuItems: SidebarMenuItem[] = [
    {
      title: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard",
      // permission: "authentication.view_customuser",
    },
    {
      title: "Consumer Sales",
      icon: <SalesIcon />,
      children: [
        {
          title: "Leads",
          icon: <DocumentLikeIcon />,
          path: "/bookings/church",
        },
        {
          title: "Request for Quote",
          icon: <DocumentIcon />,
          path: "/request-for-quote",
        },
        {
          title: "Memorial Pages",
          icon: <DocumentIcon />,
          path: "/client-portal-dashboard",
        },
        {
          title: "Bookings",
          icon: <BookingsIcon />,
          children: [
            {
              title: "Funeral",
              icon: <DocumentLikeIcon />,
              path: "/bookings/funeral",
            },

            {
              title: "Conference",
              icon: <DocumentLikeIcon />,
              path: "/bookings/conference",
            },
            {
              title: "Other",
              icon: <DocumentLikeIcon />,
              path: "/bookings/other",
            },
          ],
        },
        { title: "Contacts", icon: <ContactIcon />, path: "/contacts" },
        { title: "Proposal", icon: <DocumentIcon />, path: "/proposals" },
        {
          title: "Memory Table",
          icon: <MemoryIcon />,
          path: "/memory-table",
        },
      ],
    },
    {
      title: "B2B Sales",
      icon: <SalesIcon />,
      children: [
        {
          title: "Leads",
          icon: <DocumentLikeIcon />,
          path: "/bookings/church",
        },
        {
          title: "Funeral Homes ",
          icon: <ContactIcon />,
          path: "/bookings/funeral",
        },
        {
          title: "Churches",
          icon: <ContactIcon />,
          path: "/bookings/church",
        },
        {
          title: "Insurance Companies",
          icon: <ContactIcon />,
          path: "/bookings/church",
        },
        {
          title: "Listed Companies",
          icon: <ContactIcon />,
          path: "/bookings/church",
        },
      ],
    },
    {
      title: "Operations",
      icon: <OperationsIcon />,
      children: [
        {
          title: "Streams",
          icon: <OperationsIcon />,
          children: [
            {
              title: "List Streams",
              icon: <OperationsIcon />,
              path: "/stream-management",
            },
            {
              title: "Callsheet",
              icon: <DocumentIcon />,
              path: "/bulk-crew-assignment",
            },
          ],
        },
        {
          title: "Venues",
          icon: <LocationIcon />,
          children: [
            { title: "List Venues", icon: <LocationIcon />, path: "/venues" },
            {
              title: "Site Visit",
              icon: <LocationIcon />,
              path: "/site-visit",
            },
          ],
        },
        {
          title: "Crew Management",
          icon: <AllCrewIcon />,
          children: [
            {
              title: "Overview",
              icon: <Assessment />,
              path: "/crew-management",
            },
            {
              title: "All Crew",
              icon: <CrewIcon />,
              path: "/crew-management/all-crew",
            },
            {
              title: "Checklists",
              icon: <Assessment />,
              path: "/crew-management/checklists",
            },
            {
              title: "Feedback & Ratings",
              icon: <Assessment />,
              path: "/crew-management/crew-ratings",
            },
            {
              title: "Rates",
              icon: <TrendingUp />,
              path: "/crew-management/rates",
            },
          ],
        },
        {
          title: "Gear Management",
          icon: <GearIcon />,
          path: "/gear-management",
        },
      ],
    },

    {
      title: "Academy",
      icon: <AcademyIcon />,
      children: [
        {
          title: "Overview",
          icon: <DashboardIcon />,
          path: "/courses/overview",
        },
        {
          title: "Courses",
          icon: <OperationsIcon />,
          path: "/courses",
        },
        {
          title: "Assessments",
          icon: <OperationsIcon />,
          path: "/assessments",
        },
        {
          title: "Badges ",
          icon: <AcademyIcon />,
          path: "/badges",
        },
      ],
    },
    //  {
    //   title: "Chema",
    //   icon: (
    //     <img
    //       src={Chema}
    //       alt="Chema"
    //       style={{ width: "24px", height: "24px", objectFit: "contain" }}
    //     />
    //   ),
    //   path: "/chema/insights",
    // },
    {
      title: "Settings",
      icon: <SettingsIcon />,
      path: "/settings",
    },
    {
      title: "Configurations",
      icon: <ConfigurationIcon />,
      path: "/configurations",
    },
    {
      title: "Autolog",
      icon: <AutologIcon />,
      path: "/autolog",
    },
    {
      title: "User Admin",
      icon: <UserAdminIcon />,
      path: "/user-management",
    },
    {
      title: "Endpoint Request",
      icon: <EndpointRequestIcon />,
      path: "/endpoint-requests",
    },
  ];

  const currentWidth = isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  const sidebarContent = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.neutral_colors?.[900] || "#1a1a1a",
        position: "relative",
        transition: "all 0.3s ease",
        ...(STAGING_CONFIG.isStaging && {
          borderLeft: `6px solid ${STAGING_CONFIG.borderColor}`,
          boxShadow: `inset 6px 0 20px ${STAGING_CONFIG.glowColor}`,
        }),
      }}
    >
      <StagingTag {...STAGING_CONFIG} />

      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <CollapseButton theme={theme} />
      </Box>

      {!isCollapsed && (
        <Box sx={{ p: 3, textAlign: "center", mb: 1 }}>
          <img
            src={reduxTheme?.logo || "/static/images/logo.png"}
            alt="Logo"
            style={{
              width: "140px",
              cursor: "pointer",
              borderRadius: "5%",
            }}
          />
        </Box>
      )}

      <SidebarMenu
        items={mainMenuItems}
        theme={theme}
        onPermissionCheck={hasPermission}
      />

      <ProfileSection user={user} theme={theme} />
    </Box>
  );

  return (
    <SidebarContext.Provider
      value={{
        isCollapsed,
        toggleCollapse: () => setIsCollapsed(!isCollapsed),
        expandedMenus,
        toggleMenu: (key: string) => {
          setExpandedMenus((prev) => ({ ...prev, [key]: !prev[key] }));
        },
      }}
    >
      <Box className="sidebar" sx={{ display: "flex" }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={() => setMobileOpen(!mobileOpen)}
          sx={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 1200,
            display: { xs: "flex", md: "none" },
            backgroundColor: theme.neutral_colors?.[900] || "#1a1a1a",
            boxShadow: 1,
            "&:hover": {
              backgroundColor: theme.neutral_colors?.[800],
            },
          }}
        >
          <MenuOutlined />
        </IconButton>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              backgroundColor: theme.neutral_colors?.[900] || "#1a1a1a",
            },
          }}
        >
          {sidebarContent}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            width: currentWidth,
            flexShrink: 0,
            transition: "width 0.3s ease",
            "& .MuiDrawer-paper": {
              width: currentWidth,
              boxSizing: "border-box",
              border: "none",
              backgroundColor: theme.neutral_colors?.[900] || "#1a1a1a",
              transition: "width 0.3s ease",
              overflowX: "hidden",
            },
          }}
          open
        >
          {sidebarContent}
        </Drawer>

        <Box
          sx={{
            flex: 1,
            ml: 0,
            width: "100%",
          }}
        ></Box>
      </Box>
    </SidebarContext.Provider>
  );
};

export default Sidebar;
